import { useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  BarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { trpc } from '../lib/trpc.js';
import { formatGbp, formatPct, formatCpl } from '../lib/format.js';
import { LCC_CONSTANTS, getCplZone } from '@lcc/shared';
import { clsx } from 'clsx';

type Weeks = 4 | 8 | 12;

const COLORS = {
  navy: '#0f2942',
  gold: '#c9a961',
  green: '#16a34a',
  red: '#dc2626',
  amber: '#d97706',
  blue: '#2563eb',
  gray: '#9ca3af',
};

function ChartCard({ title, children, hint }: { title: string; children: React.ReactNode; hint?: string }) {
  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-display text-lg text-navy">{title}</h3>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          {children as React.ReactElement}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function pctChange(curr: number, prev: number): { value: number; sign: 'up' | 'down' | 'flat' } {
  if (prev === 0 && curr === 0) return { value: 0, sign: 'flat' };
  if (prev === 0) return { value: 1, sign: 'up' };
  const v = (curr - prev) / prev;
  return { value: Math.abs(v), sign: v > 0.01 ? 'up' : v < -0.01 ? 'down' : 'flat' };
}

function DeltaBadge({ curr, prev, invertColor = false }: { curr: number; prev: number; invertColor?: boolean }) {
  const { value, sign } = pctChange(curr, prev);
  if (sign === 'flat') return <span className="text-gray-400 text-xs">·</span>;
  const isGood = invertColor ? sign === 'down' : sign === 'up';
  return (
    <span className={clsx('text-xs font-mono', isGood ? 'text-green-600' : 'text-red-600')}>
      {sign === 'up' ? '↑' : '↓'}{(value * 100).toFixed(0)}%
    </span>
  );
}

export function TrendsPage() {
  const [weeks, setWeeks] = useState<Weeks>(8);
  const { data, isLoading } = trpc.metrics.weeklyMetrics.useQuery({ weeks });

  if (isLoading) return <p className="text-gray-500">Loading…</p>;
  if (!data) return <p className="text-gray-500">No data</p>;

  const chartData = data.map((w) => ({
    week: w.weekLabel,
    weekStart: w.weekStart,
    leadsCreated: w.leads.created,
    leadsBooked: w.leads.booked,
    leadsLost: w.leads.lost,
    leadsAd: w.leads.adLeads,
    bookingsCreated: w.bookings.created,
    bookingsCompleted: w.bookings.completed,
    bookingsCancelled: w.bookings.cancelled,
    bookingsNoShow: w.bookings.noShow,
    bookingsPending: w.bookings.pending,
    spend: Number(w.spend.toFixed(2)),
    revenueRealized: Number(w.revenue.realized.toFixed(2)),
    revenueExpected: Number(w.revenue.expected.toFixed(2)),
    cpl: Number(w.rates.cpl.toFixed(2)),
    costPerBooking: Number(w.rates.costPerBooking.toFixed(2)),
    aov: Number(w.rates.aov.toFixed(2)),
    leadToBookRate: Number((w.rates.leadToBookRate * 100).toFixed(1)),
    bookToCompleteRate: Number((w.rates.bookToCompleteRate * 100).toFixed(1)),
    cancelRate: Number((w.rates.cancelRate * 100).toFixed(1)),
    noShowRate: Number((w.rates.noShowRate * 100).toFixed(1)),
    avgTimeToBookHours: w.rates.avgTimeToBookHours,
    capacityPct: Number((w.capacityPct * 100).toFixed(1)),
    leadsUnattributed: Math.max(0, w.leads.created - w.leads.booked - w.leads.lost),
    // Labor
    laborHours: Number(w.labor.totalLaborHours.toFixed(1)),
    avgCrewSize: w.labor.avgCrewSize !== null ? Number(w.labor.avgCrewSize.toFixed(1)) : null,
    avgActualMin: w.labor.avgActualDurationMin !== null ? Math.round(w.labor.avgActualDurationMin) : null,
    avgEstMin: w.labor.avgEstimatedDurationMin !== null ? Math.round(w.labor.avgEstimatedDurationMin) : null,
    revenuePerLaborHour: w.labor.revenuePerLaborHour !== null ? Number(w.labor.revenuePerLaborHour.toFixed(2)) : null,
    avgDurationDriftMin: w.labor.avgDurationDriftMin !== null ? Math.round(w.labor.avgDurationDriftMin) : null,
  }));

  const lastWeek = data[data.length - 1]!;
  const prevWeek = data[data.length - 2];

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="font-display text-3xl text-navy">Trends</h1>
          <p className="text-sm text-gray-500">Weekly rolling — Mon → Sun, Europe/London. Bookings bucketed by scheduled date.</p>
        </div>
        <div className="flex gap-1">
          {([4, 8, 12] as const).map((w) => (
            <button
              key={w}
              onClick={() => setWeeks(w)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-xs font-medium border',
                weeks === w ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-gray-200',
              )}
            >
              {w}w
            </button>
          ))}
        </div>
      </div>

      {/* Summary tiles for the latest week */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryTile label="Leads (this week)" value={lastWeek.leads.created} prev={prevWeek?.leads.created ?? 0} />
        <SummaryTile label="Bookings scheduled" value={lastWeek.bookings.created} prev={prevWeek?.bookings.created ?? 0} />
        <SummaryTile label="Completed jobs" value={lastWeek.bookings.completed} prev={prevWeek?.bookings.completed ?? 0} />
        <SummaryTile
          label="CPL"
          value={lastWeek.rates.cpl > 0 ? formatCpl(lastWeek.rates.cpl) : '—'}
          prev={prevWeek?.rates.cpl ?? 0}
          rawCurr={lastWeek.rates.cpl}
          invertColor
        />
        <SummaryTile
          label="Cost / booking"
          value={lastWeek.rates.costPerBooking > 0 ? formatGbp(lastWeek.rates.costPerBooking) : '—'}
          prev={prevWeek?.rates.costPerBooking ?? 0}
          rawCurr={lastWeek.rates.costPerBooking}
          invertColor
        />
        <SummaryTile
          label="AOV (completed)"
          value={lastWeek.rates.aov > 0 ? formatGbp(lastWeek.rates.aov) : '—'}
          prev={prevWeek?.rates.aov ?? 0}
          rawCurr={lastWeek.rates.aov}
        />
        <SummaryTile
          label="Lead → book rate"
          value={formatPct(lastWeek.rates.leadToBookRate)}
          prev={prevWeek?.rates.leadToBookRate ?? 0}
          rawCurr={lastWeek.rates.leadToBookRate}
        />
        <SummaryTile
          label="Avg time to book"
          value={lastWeek.rates.avgTimeToBookHours !== null ? `${lastWeek.rates.avgTimeToBookHours.toFixed(1)}h` : '—'}
          prev={prevWeek?.rates.avgTimeToBookHours ?? 0}
          rawCurr={lastWeek.rates.avgTimeToBookHours ?? 0}
          invertColor
        />
      </div>

      {/* Labor summary tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryTile
          label="Labor hours (this week)"
          value={lastWeek.labor.totalLaborHours > 0 ? `${lastWeek.labor.totalLaborHours.toFixed(1)}h` : '—'}
          prev={prevWeek?.labor.totalLaborHours ?? 0}
          rawCurr={lastWeek.labor.totalLaborHours}
        />
        <SummaryTile
          label="Avg crew per job"
          value={lastWeek.labor.avgCrewSize !== null ? lastWeek.labor.avgCrewSize.toFixed(1) : '—'}
          prev={prevWeek?.labor.avgCrewSize ?? 0}
          rawCurr={lastWeek.labor.avgCrewSize ?? 0}
        />
        <SummaryTile
          label="Avg job duration"
          value={lastWeek.labor.avgActualDurationMin !== null ? `${Math.round(lastWeek.labor.avgActualDurationMin)}m` : '—'}
          prev={prevWeek?.labor.avgActualDurationMin ?? 0}
          rawCurr={lastWeek.labor.avgActualDurationMin ?? 0}
        />
        <SummaryTile
          label="Revenue / labor hour"
          value={lastWeek.labor.revenuePerLaborHour !== null ? formatGbp(lastWeek.labor.revenuePerLaborHour) : '—'}
          prev={prevWeek?.labor.revenuePerLaborHour ?? 0}
          rawCurr={lastWeek.labor.revenuePerLaborHour ?? 0}
        />
      </div>

      {/* Multi-week charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Leads & bookings" hint="Bars: leads · Lines: bookings scheduled / completed">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="leadsCreated" name="Leads" fill={COLORS.gold} radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="bookingsCreated" name="Bookings scheduled" stroke={COLORS.navy} strokeWidth={2} />
            <Line type="monotone" dataKey="bookingsCompleted" name="Completed" stroke={COLORS.green} strokeWidth={2} strokeDasharray="3 3" />
          </ComposedChart>
        </ChartCard>

        <ChartCard title="Spend & revenue" hint="£ realized vs expected">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
            <Tooltip formatter={(v: number) => formatGbp(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="spend" name="Ad spend" fill={COLORS.red} radius={[4, 4, 0, 0]} />
            <Line type="monotone" dataKey="revenueRealized" name="Revenue (completed)" stroke={COLORS.green} strokeWidth={2} />
            <Line type="monotone" dataKey="revenueExpected" name="Revenue (booked, all)" stroke={COLORS.blue} strokeWidth={2} strokeDasharray="3 3" />
          </ComposedChart>
        </ChartCard>

        <ChartCard title="CPL & cost per booking" hint="Marketing efficiency">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `£${v}`} />
            <Tooltip formatter={(v: number) => formatCpl(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="cpl" name="CPL" stroke={COLORS.amber} strokeWidth={2} />
            <Line type="monotone" dataKey="costPerBooking" name="Cost / booking" stroke={COLORS.navy} strokeWidth={2} />
          </ComposedChart>
        </ChartCard>

        <ChartCard title="Conversion rates" hint="Lead → book · Book → complete">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
            <Tooltip formatter={(v: number) => `${v}%`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="leadToBookRate" name="Lead → book" stroke={COLORS.gold} strokeWidth={2} />
            <Line type="monotone" dataKey="bookToCompleteRate" name="Book → complete" stroke={COLORS.green} strokeWidth={2} />
            <Line type="monotone" dataKey="cancelRate" name="Cancel" stroke={COLORS.red} strokeWidth={2} strokeDasharray="3 3" />
          </ComposedChart>
        </ChartCard>
      </div>

      {/* Labor charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Labor hours & crew" hint="Bars: total labor hrs · Line: avg crew per job">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}h`} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 'dataMax + 1']} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar yAxisId="left" dataKey="laborHours" name="Labor hrs" fill={COLORS.navy} radius={[4, 4, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="avgCrewSize" name="Avg crew" stroke={COLORS.gold} strokeWidth={2} />
          </ComposedChart>
        </ChartCard>

        <ChartCard title="Job duration: estimated vs actual" hint="Minutes per job — drift = estimating accuracy">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}m`} />
            <Tooltip formatter={(v: number) => `${v} min`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="avgEstMin" name="Estimated" fill={COLORS.gray} radius={[4, 4, 0, 0]} />
            <Bar dataKey="avgActualMin" name="Actual" fill={COLORS.green} radius={[4, 4, 0, 0]} />
          </ComposedChart>
        </ChartCard>
      </div>

      {/* Funnel breakdown — stacked bars per week */}
      <ChartCard
        title="Conversion funnel by week"
        hint="Of this week's leads → how many booked → completed · grey = still pending or unmatched"
      >
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="leadsBooked" stackId="funnel" name="Booked" fill={COLORS.green} />
          <Bar dataKey="leadsLost" stackId="funnel" name="Lost" fill={COLORS.red} />
          <Bar dataKey="leadsUnattributed" stackId="funnel" name="Open / unmatched" fill={COLORS.gray} />
        </BarChart>
      </ChartCard>

      {/* Week-over-week comparison table */}
      <div className="card overflow-x-auto">
        <h3 className="font-display text-lg text-navy mb-3">Week-over-week</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-xs text-gray-500">
              <th className="text-left py-2 px-2 font-medium">Metric</th>
              {chartData.map((w) => (
                <th key={w.weekStart} className="text-right py-2 px-2 font-medium font-mono">{w.week}</th>
              ))}
            </tr>
          </thead>
          <tbody className="text-sm">
            <Row label="Leads created" values={chartData.map((w) => w.leadsCreated)} />
            <Row label="Leads booked" values={chartData.map((w) => w.leadsBooked)} />
            <Row label="Lead → book rate" values={chartData.map((w) => `${w.leadToBookRate}%`)} />
            <Row label="Bookings scheduled" values={chartData.map((w) => w.bookingsCreated)} />
            <Row label="Completed" values={chartData.map((w) => w.bookingsCompleted)} />
            <Row label="Cancelled" values={chartData.map((w) => w.bookingsCancelled)} />
            <Row label="No-show" values={chartData.map((w) => w.bookingsNoShow)} />
            <Row label="Book → complete" values={chartData.map((w) => `${w.bookToCompleteRate}%`)} />
            <Row label="Spend" values={chartData.map((w) => formatGbp(w.spend))} />
            <Row label="Revenue (realized)" values={chartData.map((w) => formatGbp(w.revenueRealized))} />
            <Row label="CPL" values={chartData.map((w) => w.cpl > 0 ? formatCpl(w.cpl) : '—')} cplZones={chartData.map((w) => w.cpl > 0 ? getCplZone(w.cpl) : null)} />
            <Row label="Cost / booking" values={chartData.map((w) => w.costPerBooking > 0 ? formatGbp(w.costPerBooking) : '—')} />
            <Row label="AOV" values={chartData.map((w) => w.aov > 0 ? formatGbp(w.aov) : '—')} />
            <Row label="Avg time → book" values={chartData.map((w) => w.avgTimeToBookHours !== null ? `${w.avgTimeToBookHours.toFixed(1)}h` : '—')} />
            <Row label="Capacity" values={chartData.map((w) => `${w.capacityPct}%`)} highlightCapacity />
            <Row label="Avg crew / job" values={chartData.map((w) => w.avgCrewSize !== null ? w.avgCrewSize.toFixed(1) : '—')} />
            <Row label="Avg duration (actual)" values={chartData.map((w) => w.avgActualMin !== null ? `${w.avgActualMin}m` : '—')} />
            <Row label="Avg duration (est)" values={chartData.map((w) => w.avgEstMin !== null ? `${w.avgEstMin}m` : '—')} />
            <Row label="Drift (act − est)" values={chartData.map((w) => w.avgDurationDriftMin !== null ? `${w.avgDurationDriftMin > 0 ? '+' : ''}${w.avgDurationDriftMin}m` : '—')} />
            <Row label="Total labor hrs" values={chartData.map((w) => w.laborHours > 0 ? `${w.laborHours}h` : '—')} />
            <Row label="Revenue / labor hr" values={chartData.map((w) => w.revenuePerLaborHour !== null ? formatGbp(w.revenuePerLaborHour) : '—')} />
          </tbody>
        </table>
        <p className="text-xs text-gray-400 mt-3">
          Capacity ceiling: {LCC_CONSTANTS.CAPACITY_JOBS_PER_WEEK} jobs/week. CPL zones use thresholds defined in shared constants.
        </p>
      </div>
    </div>
  );
}

function SummaryTile({
  label, value, prev, rawCurr, invertColor,
}: {
  label: string;
  value: string | number;
  prev: number;
  rawCurr?: number;
  invertColor?: boolean;
}) {
  const numericCurr = rawCurr ?? (typeof value === 'number' ? value : 0);
  return (
    <div className="card">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <div className="flex items-baseline justify-between">
        <p className="text-2xl font-display text-navy">{value}</p>
        <DeltaBadge curr={numericCurr} prev={prev} invertColor={invertColor} />
      </div>
      <p className="text-xs text-gray-400 mt-1 font-mono">prev: {typeof prev === 'number' && prev > 0 ? (prev < 100 ? prev.toFixed(prev < 10 ? 1 : 0) : Math.round(prev)) : '—'}</p>
    </div>
  );
}

function Row({
  label, values, cplZones, highlightCapacity,
}: {
  label: string;
  values: (string | number)[];
  cplZones?: (string | null)[];
  highlightCapacity?: boolean;
}) {
  return (
    <tr className="border-b border-gray-50 hover:bg-cream-tint">
      <td className="py-1.5 px-2 text-gray-700">{label}</td>
      {values.map((v, i) => {
        const zone = cplZones?.[i];
        const cls = clsx(
          'py-1.5 px-2 text-right font-mono',
          zone === 'GREEN' && 'text-green-700',
          zone === 'YELLOW' && 'text-amber-700',
          zone === 'RED' && 'text-red-700',
          highlightCapacity && typeof v === 'string' && parseInt(v) > 90 && 'text-red-700',
          highlightCapacity && typeof v === 'string' && parseInt(v) > 70 && parseInt(v) <= 90 && 'text-amber-700',
        );
        return <td key={i} className={cls}>{v}</td>;
      })}
    </tr>
  );
}
